import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@/lib/api/hooks';

export type SizeBand = 'S-XL' | '2XL' | '3XL' | '4XL' | '5XL';

/**
 * Returns the size bands SS Activewear carries for a given style. Gated
 * on non-empty `style`. Shares React Query cache with `ColorCombobox`
 * (same queryKey) so there's no extra API call — the combobox's fetch
 * and this hook consume the same `/garments/by-style/:style` response.
 *
 * Returns `undefined` when no style is picked or the style lookup is
 * in-flight / errored — callers should fall back to a default (3-bucket
 * S-XL / 2XL / 3XL) UX in those cases.
 */
export function useStyleBands(style: string): SizeBand[] | undefined {
  const client = useApiClient();
  const trimmed = style.trim();
  const { data } = useQuery({
    queryKey: ['style-colors', trimmed],
    enabled: trimmed.length > 0,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await client.GET('/garments/by-style/{style}', {
        params: { path: { style: trimmed } },
      });
      if (error) throw error;
      return data.data;
    },
  });
  return data?.availableBands as SizeBand[] | undefined;
}
